/* eslint-disable no-console */
import { FC, useCallback, useEffect, useState } from 'react';
import { DraggableTabList, Tab } from 'components/draggable-tab-list/DraggableTabList';
import { useAppDispatch, useAppSelector } from 'hooks/typedHooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { selectCurrentCustomerView, setCurrentCustomerView, setCustomerViewFilters } from '../customersSlice';
import { CustomerView } from '../customers.model';
import { useUpdateCustomerViewPriorityMutation } from '../api';
import { areEqualArrays, throttle } from 'utils/commonUtils';

interface CustomerViewsProps {
  views: CustomerView[];
  isTeamViewsLoading: boolean;
  handleViewChange: ({
    viewId,
    columns,
    sortBy,
    sortOrder
  }: {
    viewId: string;
    columns: string[];
    sortBy: string;
    sortOrder: string;
  }) => void;
}

const CustomerViews: FC<CustomerViewsProps> = ({ views, handleViewChange }) => {
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentView = useAppSelector(selectCurrentCustomerView);
  const [updateCustomerViewPriority] = useUpdateCustomerViewPriorityMutation();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [viewTabs, setViewTabs] = useState<Tab[]>();

  useEffect(() => {
    setViewTabs(
      views
        .filter(view => view.isActive)
        .map(view => ({
          id: view.id.toString(),
          label: view.name,
          count: 0,
          showClose: false
        }))
    );
    const lastViewed = views.find(view => view.isLastViewed) ?? views[0];
    dispatch(setCurrentCustomerView(lastViewed));
    dispatch(setCustomerViewFilters(lastViewed?.filters));
    const sortBy = lastViewed?.sortBy.map(sort => sort.key).join(',');
    const sortOrder = lastViewed?.sortBy.map(sort => sort.order).join(',') as 'asc' | 'desc';
    handleViewChange({
      viewId: lastViewed?.id.toString(),
      columns: lastViewed?.columnFocus,
      sortBy,
      sortOrder
    });
  }, [views]);

  useEffect(() => {
    if (currentView) {
      setActiveTab(currentView.id.toString());
    }
  }, [currentView?.id]);

  const handleTabReorder = useCallback(
    async (newOrder: Tab[], oldOrder: Tab[]) => {
      const viewIds = newOrder.map(tab => parseInt(tab.id));
      const oldViewIds = oldOrder.map(tab => parseInt(tab.id));
      if (!areEqualArrays({ primaryArray: viewIds || [], secondaryArray: oldViewIds || [] })) {
        const order = viewIds.map((id, index) => ({
          id,
          priority: index + 1
        }));
        try {
          await updateCustomerViewPriority({ teamId: currentTeam?.id.toString(), order });
          setViewTabs(newOrder);
        } catch (error) {
          setViewTabs(oldOrder);
        }
      }
    },
    [currentTeam?.id, updateCustomerViewPriority]
  );

  const handleTabChangeInternal = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      const selectedView = views.find(view => view.id.toString() === tabId);

      if (selectedView) {
        dispatch(setCurrentCustomerView(selectedView));
        const sortBy = selectedView.sortBy.map(sort => sort.key).join(',');
        const sortOrder = selectedView.sortBy.map(sort => sort.order).join(',') as 'asc' | 'desc';
        handleViewChange({ viewId: selectedView.id.toString(), columns: selectedView.columnFocus, sortBy, sortOrder });
      }
    },
    [dispatch, views, handleViewChange]
  );

  const onTabChange = useCallback(
    throttle((tabId: string) => handleTabChangeInternal(tabId), 300),
    [handleTabChangeInternal]
  );

  return (
    <div className="flex w-full items-center">
      <div className="h-full w-full max-w-full border-y border-borderGray">
        <DraggableTabList
          tabs={viewTabs || []}
          activeTab={activeTab}
          onTabChange={onTabChange}
          onReorder={handleTabReorder}
          className="flex w-full"
          tabClassName="flex-1 min-w-[200px]"
        />
      </div>
    </div>
  );
};

export default CustomerViews;
